import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  Card,
  Button,
  Chip,
  RadioButton,
  Checkbox,
  TextInput,
  useTheme,
  Divider,
  IconButton,
  HelperText,
} from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type {
  JourneyCustomizationPanelProps,
  JourneyCustomization,
  CustomizationOption,
  CustomizationChoice,
} from './JourneyPreviewTypes';

export function JourneyCustomizationPanel({
  journey,
  options,
  onCustomize,
  onPreview,
  isLoading = false,
  style,
}: JourneyCustomizationPanelProps) {
  const theme = useTheme();
  const [customizations, setCustomizations] = useState<JourneyCustomization[]>(
    []
  );
  const [expandedOptions, setExpandedOptions] = useState<Set<string>>(
    new Set()
  );

  // Initialize customizations with current values
  useEffect(() => {
    const initialCustomizations = options.map(option => ({
      optionId: option.id,
      newValue: option.currentValue,
    }));
    setCustomizations(initialCustomizations);
  }, [options]);

  const handleCustomizationChange = (
    optionId: string,
    newValue: any,
    reasoning?: string
  ) => {
    const updatedCustomizations = customizations.map(custom =>
      custom.optionId === optionId ? { ...custom, newValue, reasoning } : custom
    );
    setCustomizations(updatedCustomizations);

    // Notify parent of the change
    const customization = { optionId, newValue, reasoning };
    onCustomize(customization);
  };

  const handlePreview = () => {
    onPreview(customizations);
  };

  const toggleOptionExpanded = (optionId: string) => {
    const newExpanded = new Set(expandedOptions);
    if (newExpanded.has(optionId)) {
      newExpanded.delete(optionId);
    } else {
      newExpanded.add(optionId);
    }
    setExpandedOptions(newExpanded);
  };

  const getImpactColor = (impact: number) => {
    if (Math.abs(impact) < 0.1) return theme.colors.outline;
    if (impact > 0) return theme.colors.primary;
    return theme.colors.error;
  };

  const getImpactIcon = (impact: number) => {
    if (Math.abs(impact) < 0.1) return 'minus';
    if (impact > 0) return 'arrow-up';
    return 'arrow-down';
  };

  const formatImpactText = (impact: number, label: string) => {
    if (Math.abs(impact) < 0.1) return `No change to ${label.toLowerCase()}`;
    const direction = impact > 0 ? 'increase' : 'decrease';
    const magnitude = Math.abs(impact) > 0.3 ? 'significant' : 'moderate';
    return `${magnitude} ${direction} in ${label.toLowerCase()}`;
  };

  const renderCustomizationOption = (option: CustomizationOption) => {
    const isExpanded = expandedOptions.has(option.id);
    const currentCustomization = customizations.find(
      c => c.optionId === option.id
    );
    const currentValue = currentCustomization?.newValue ?? option.currentValue;

    return (
      <Card key={option.id} style={styles.optionCard} mode='outlined'>
        <Card.Content>
          {/* Option Header */}
          <View style={styles.optionHeader}>
            <View style={styles.optionTitleContainer}>
              <Text variant='titleSmall' style={styles.optionTitle}>
                {option.label}
              </Text>
              <Text variant='bodySmall' style={styles.optionDescription}>
                {option.description}
              </Text>
            </View>
            <IconButton
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              onPress={() => toggleOptionExpanded(option.id)}
            />
          </View>

          {/* Current Value Display */}
          <View style={styles.currentValueContainer}>
            <Text variant='labelSmall' style={styles.currentValueLabel}>
              Current: {formatOptionValue(option.type, currentValue)}
            </Text>
          </View>

          {/* Customization Controls */}
          <View style={styles.customizationControls}>
            {renderCustomizationControl(option, currentValue)}
          </View>

          {/* Impact Preview */}
          {isExpanded && (
            <View style={styles.impactSection}>
              <Divider style={styles.divider} />
              <Text variant='labelMedium' style={styles.impactTitle}>
                Impact Preview
              </Text>
              <Text variant='bodySmall' style={styles.impactDescription}>
                {option.impact.description}
              </Text>

              <View style={styles.impactMetrics}>
                {renderImpactMetric(
                  'Engagement',
                  option.impact.engagementChange
                )}
                {renderImpactMetric(
                  'Difficulty',
                  option.impact.difficultyChange
                )}
                {renderImpactMetric(
                  'Time Commitment',
                  option.impact.timeCommitmentChange
                )}
                {renderImpactMetric(
                  'Skill Development',
                  option.impact.skillDevelopmentChange
                )}
              </View>
            </View>
          )}
        </Card.Content>
      </Card>
    );
  };

  const renderCustomizationControl = (
    option: CustomizationOption,
    currentValue: any
  ) => {
    switch (option.type) {
      case 'duration':
        return (
          <View style={styles.sliderContainer}>
            <Text variant='labelSmall' style={styles.sliderLabel}>
              Duration: {currentValue} weeks
            </Text>
            <Text variant='bodySmall' style={styles.sliderNote}>
              Use buttons to adjust duration (Slider component not available)
            </Text>
            <View style={styles.sliderButtons}>
              <Button
                mode='outlined'
                onPress={() =>
                  handleCustomizationChange(
                    option.id,
                    Math.max(2, currentValue - 1)
                  )
                }
                disabled={currentValue <= 2}
                compact
              >
                -
              </Button>
              <Text variant='bodyMedium' style={styles.sliderValue}>
                {currentValue} weeks
              </Text>
              <Button
                mode='outlined'
                onPress={() =>
                  handleCustomizationChange(
                    option.id,
                    Math.min(24, currentValue + 1)
                  )
                }
                disabled={currentValue >= 24}
                compact
              >
                +
              </Button>
            </View>
            <View style={styles.sliderLabels}>
              <Text variant='bodySmall' style={styles.sliderLabelText}>
                2 weeks
              </Text>
              <Text variant='bodySmall' style={styles.sliderLabelText}>
                24 weeks
              </Text>
            </View>
          </View>
        );

      case 'focus_areas':
        return (
          <View style={styles.checkboxContainer}>
            {option.options.map((choice: CustomizationChoice) => (
              <View key={choice.value} style={styles.checkboxItem}>
                <Checkbox
                  status={
                    currentValue.includes(choice.value)
                      ? 'checked'
                      : 'unchecked'
                  }
                  onPress={() => {
                    const newValue = currentValue.includes(choice.value)
                      ? currentValue.filter((v: any) => v !== choice.value)
                      : [...currentValue, choice.value];
                    handleCustomizationChange(
                      option.id,
                      newValue,
                      choice.reasoning
                    );
                  }}
                />
                <View style={styles.checkboxContent}>
                  <Text variant='bodyMedium'>{choice.label}</Text>
                  <Text variant='bodySmall' style={styles.checkboxDescription}>
                    {choice.description}
                  </Text>
                  <ConfidenceIndicator
                    confidence={choice.confidence}
                    showLabel={false}
                    size='small'
                    style={styles.choiceConfidence}
                  />
                </View>
              </View>
            ))}
          </View>
        );

      case 'difficulty':
      case 'content_types':
      case 'schedule':
        return (
          <RadioButton.Group
            onValueChange={value => {
              const selectedChoice = option.options.find(
                c => c.value === value
              );
              handleCustomizationChange(
                option.id,
                value,
                selectedChoice?.reasoning
              );
            }}
            value={currentValue}
          >
            {option.options.map((choice: CustomizationChoice) => (
              <View key={choice.value} style={styles.radioItem}>
                <RadioButton value={choice.value} />
                <View style={styles.radioContent}>
                  <Text variant='bodyMedium'>{choice.label}</Text>
                  <Text variant='bodySmall' style={styles.radioDescription}>
                    {choice.description}
                  </Text>
                  <ConfidenceIndicator
                    confidence={choice.confidence}
                    showLabel={false}
                    size='small'
                    style={styles.choiceConfidence}
                  />
                </View>
              </View>
            ))}
          </RadioButton.Group>
        );

      default:
        return (
          <TextInput
            mode='outlined'
            value={String(currentValue)}
            onChangeText={text => handleCustomizationChange(option.id, text)}
            style={styles.textInput}
          />
        );
    }
  };

  const renderImpactMetric = (label: string, impact: number) => (
    <View key={label} style={styles.impactMetric}>
      <IconButton
        icon={getImpactIcon(impact)}
        size={16}
        iconColor={getImpactColor(impact)}
        style={styles.impactIcon}
      />
      <View style={styles.impactContent}>
        <Text variant='bodySmall' style={styles.impactLabel}>
          {label}
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.impactText, { color: getImpactColor(impact) }]}
        >
          {formatImpactText(impact, label)}
        </Text>
      </View>
    </View>
  );

  const formatOptionValue = (type: string, value: any) => {
    switch (type) {
      case 'duration':
        return `${value} weeks`;
      case 'focus_areas':
        return Array.isArray(value) ? `${value.length} areas selected` : value;
      case 'difficulty':
        return String(value).charAt(0).toUpperCase() + String(value).slice(1);
      default:
        return String(value);
    }
  };

  const hasChanges = customizations.some(custom => {
    const option = options.find(o => o.id === custom.optionId);
    return (
      option &&
      JSON.stringify(custom.newValue) !== JSON.stringify(option.currentValue)
    );
  });

  return (
    <View style={[styles.container, style]}>
      <View style={styles.header}>
        <Text variant='titleMedium' style={styles.title}>
          Customize Your Journey
        </Text>
        <Text variant='bodySmall' style={styles.subtitle}>
          Adjust these settings to better match your preferences
        </Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {options.map(renderCustomizationOption)}
      </ScrollView>

      <View style={styles.footer}>
        {hasChanges && (
          <HelperText type='info' style={styles.changesHelper}>
            💡 Changes will be applied when you preview or accept the journey
          </HelperText>
        )}

        <View style={styles.actions}>
          <Button
            mode='outlined'
            onPress={handlePreview}
            disabled={isLoading || !hasChanges}
            loading={isLoading}
            style={styles.previewButton}
            icon='eye'
          >
            Preview Changes
          </Button>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontWeight: '600',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 16,
  },
  optionCard: {
    marginBottom: 12,
  },
  optionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  optionTitleContainer: {
    flex: 1,
  },
  optionTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  optionDescription: {
    opacity: 0.7,
    lineHeight: 16,
  },
  currentValueContainer: {
    marginBottom: 12,
  },
  currentValueLabel: {
    opacity: 0.8,
    fontWeight: '500',
  },
  customizationControls: {
    marginBottom: 8,
  },
  sliderContainer: {
    marginVertical: 8,
  },
  sliderLabel: {
    marginBottom: 8,
    fontWeight: '500',
  },
  sliderNote: {
    opacity: 0.7,
    marginBottom: 8,
    fontStyle: 'italic',
  },
  sliderButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  sliderValue: {
    minWidth: 80,
    textAlign: 'center',
    fontWeight: '500',
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  sliderLabelText: {
    opacity: 0.6,
  },
  checkboxContainer: {
    gap: 8,
  },
  checkboxItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 4,
  },
  checkboxContent: {
    flex: 1,
    marginLeft: 8,
  },
  checkboxDescription: {
    opacity: 0.7,
    marginTop: 2,
    lineHeight: 16,
  },
  radioItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
  },
  radioContent: {
    flex: 1,
    marginLeft: 8,
  },
  radioDescription: {
    opacity: 0.7,
    marginTop: 2,
    lineHeight: 16,
  },
  choiceConfidence: {
    marginTop: 4,
  },
  textInput: {
    marginVertical: 8,
  },
  impactSection: {
    marginTop: 8,
  },
  divider: {
    marginVertical: 12,
  },
  impactTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  impactDescription: {
    opacity: 0.8,
    marginBottom: 12,
    lineHeight: 16,
  },
  impactMetrics: {
    gap: 8,
  },
  impactMetric: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  impactIcon: {
    margin: 0,
    marginRight: 8,
  },
  impactContent: {
    flex: 1,
  },
  impactLabel: {
    fontWeight: '500',
    marginBottom: 2,
  },
  impactText: {
    fontSize: 11,
    opacity: 0.8,
  },
  footer: {
    padding: 16,
    paddingTop: 8,
  },
  changesHelper: {
    marginBottom: 8,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  previewButton: {
    minWidth: 150,
  },
});
